import type { IComponentController, IScope } from "angular";
import { finalize } from "rxjs/operators";

import { cloneDeep, isEmpty } from "lodash";

import type {
    EquipmentClass,
    IEquipmentTypeSlim,
    IManufacturer,
    IResponseWithLoading,
    IrMap,
} from "App/Modules/Models";
import { EquipmentClassFunctions } from "App/Modules/Models";
import type { IEquipmentTypeRepository, IManufacturersRepository } from "App/Modules/Repositories";
import type { IFormContainerResolve } from "App/Modules/Services";
import { extendWithLoadingAndFail } from "App/Modules/Services/ObservableExtensions";

export default class EditEquipmentTypeInformationComponentController implements IComponentController {
    public static $inject: string[] = ["$scope", "irManufacturersRepository", "mmEquipmentTypeRepository"];

    public resolve: IFormContainerResolve<IEquipmentTypeSlim, void>;

    public editResponse: IResponseWithLoading<IEquipmentTypeSlim>;

    public deleteResponse: IResponseWithLoading<void>;

    public equipmentType: IEquipmentTypeSlim;

    public originalEquipmentType: IEquipmentTypeSlim;

    public manufacturers: IResponseWithLoading<IManufacturer[]>;

    public equipmentClasses: IrMap<EquipmentClass, string>;

    constructor(
        private readonly $scope: IScope,
        private readonly manufacturersRepository: IManufacturersRepository,
        private readonly equipmentTypeRepository: IEquipmentTypeRepository,
    ) {}

    public $onInit() {
        this.originalEquipmentType = this.resolve.formData;
        this.equipmentType = cloneDeep(this.originalEquipmentType);
        extendWithLoadingAndFail(this.manufacturersRepository.getManufacturers())
            .pipe(finalize(() => this.$scope.$applyAsync()))
            .subscribe(
                (response: IResponseWithLoading<IManufacturer[]>) => {
                    this.manufacturers = response;
                },
                (error) => {
                    this.manufacturers = error;
                },
            );

        this.equipmentClasses = EquipmentClassFunctions.GetEquipmentClassDictionary();
    }

    public editEquipmentType() {
        const patchObject = {} as IEquipmentTypeSlim;

        if (this.originalEquipmentType.manufacturer.id !== this.equipmentType.manufacturer.id) {
            patchObject.manufacturer = this.equipmentType.manufacturer;
        }

        if (this.originalEquipmentType.model !== this.equipmentType.model) {
            patchObject.model = this.equipmentType.model;
        }

        if (this.originalEquipmentType.version !== this.equipmentType.version) {
            patchObject.version = this.equipmentType.version;
        }

        if (this.originalEquipmentType.class !== this.equipmentType.class) {
            patchObject.class = this.equipmentType.class;
        }

        if (!isEmpty(patchObject)) {
            extendWithLoadingAndFail(
                this.equipmentTypeRepository.patchEquipmentType(this.equipmentType.id, patchObject),
            )
                .pipe(finalize(() => this.$scope.$applyAsync()))
                .subscribe(
                    (editResponse: IResponseWithLoading<IEquipmentTypeSlim>) => {
                        this.editResponse = editResponse;
                        if (!editResponse.isLoading && editResponse.data) {
                            this.resolve.options.onOk(editResponse.data);
                        }
                    },
                    (errorResponse) => {
                        this.editResponse = errorResponse;
                    },
                );
        } else {
            this.cancel();
        }
    }

    public cancel() {
        if (this.resolve.options.onCancel) {
            this.resolve.options.onCancel();
        }
    }

    public deleteEquipmentType() {
        extendWithLoadingAndFail(this.equipmentTypeRepository.deleteEquipmentType(this.equipmentType.id))
            .pipe(finalize(() => this.$scope.$applyAsync()))
            .subscribe(
                (deleteResponse: IResponseWithLoading<void>) => {
                    this.deleteResponse = deleteResponse;
                    if (!deleteResponse.isLoading) {
                        this.resolve.options.onOk(null);
                    }
                },
                (errorResponse) => {
                    this.editResponse = errorResponse;
                    this.deleteResponse = errorResponse;
                },
            );
    }
}
